import React, { useMemo, useState } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { formatDistanceStrict, parseISO } from "date-fns";
import { enUS, ru, uk } from "date-fns/locale";

// ---------- i18n ----------
const dict = {
    en: {
        columns: { status: "Status", from: "From", until: "Until", range: "Time Range" },
        active: "Active",
        inactive: "Inactive",
        show: "Show",
        entries: "entries",
        pageXofY: (x, y) => `Page ${x} of ${y}`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
    },
    ru: {
        columns: { status: "Статус", from: "С", until: "По", range: "Интервал" },
        active: "Активно",
        inactive: "Неактивно",
        show: "Показать",
        entries: "записей",
        pageXofY: (x, y) => `Страница ${x} из ${y}`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
    },
    ua: {
        columns: { status: "Статус", from: "Від", until: "До", range: "Інтервал" },
        active: "Активно",
        inactive: "Неактивно",
        show: "Показати",
        entries: "записів",
        pageXofY: (x, y) => `Сторінка ${x} з ${y}`,
        first: "<<",
        prev: "<",
        next: ">",
        last: ">>",
    },
};

const dateFnsLocales = { en: enUS, ru, ua: uk };
const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// ---------- data (demo) ----------
// ---------- table ----------
const StatusTable = ({ lang: langProp, statusPeriods = [] }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);
    const dfLocale = dateFnsLocales[lang] || enUS;

    const normalized = (statusPeriods || []).map((item) => {
        return {
            status: String(item.status) === "1" ? "Active" : "Inactive",
            from: item.from_date,
            until: item.to_date,
        };
    });

    const data = React.useMemo(() => normalized, [statusPeriods]);
    const columns = React.useMemo(
        () => [
            {
                Header: t.columns.status,
                accessor: "status",
                Cell: ({ value }) => {
                    const isActive = value === "Active";
                    // показываем перевод независимо от исходного значения
                    const label = isActive ? t.active : t.inactive;
                    return (
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span
                  style={{
                      width: 10, height: 10, borderRadius: "50%",
                      backgroundColor: isActive ? "green" : "brown",
                  }}
              />
                            {label}
                        </div>
                    );
                },
            },
            {
                Header: t.columns.from,
                accessor: "from",
                Cell: ({ value }) => new Date(value).toLocaleString(lang),
            },
            {
                Header: t.columns.until,
                accessor: "until",
                Cell: ({ value }) => new Date(value).toLocaleString(lang),
            },
            {
                Header: t.columns.range,
                accessor: (row) => {
                    const from = parseISO(row.from);
                    const until = parseISO(row.until);
                    return formatDistanceStrict(from, until, { locale: dfLocale });
                },
            },
        ],
        [t, lang, dfLocale]
    );

    const {
        getTableProps, getTableBodyProps, headerGroups, page, prepareRow,
        canPreviousPage, canNextPage, pageOptions, nextPage, previousPage,
        gotoPage, setPageSize, state: { pageIndex, pageSize },
    } = useTable(
        { columns, data, initialState: { pageSize: 5 } },
        useSortBy,
        usePagination
    );

    return (
        <div style={{ padding: 20 }}>
            <div style={{ marginBottom: 10 }}>
                <label>
                    {t.show}{" "}
                    <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                    </select>{" "}
                    {t.entries}
                </label>
            </div>

            <table
                {...getTableProps()}
                style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", border: "1px solid #ddd" }}
            >
                <thead>
                {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column) => (
                            <th
                                {...column.getHeaderProps(column.getSortByToggleProps())}
                                style={{ background: "#3f51b5", color: "white", padding: 10, border: "1px solid #ddd" }}
                            >
                                {column.render("Header")}
                                <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                    prepareRow(row);
                    return (
                        <tr
                            {...row.getRowProps()}
                            style={{ backgroundColor: row.index % 2 === 0 ? "#f9f9f9" : "#fff" }}
                        >
                            {row.cells.map((cell) => (
                                <td {...cell.getCellProps()} style={{ padding: 10, border: "1px solid #ddd" }}>
                                    {cell.render("Cell")}
                                </td>
                            ))}
                        </tr>
                    );
                })}
                </tbody>
            </table>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
                <div>{t.pageXofY(pageIndex + 1, pageOptions.length)}</div>
                <div>
                    <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{t.first}</button>{" "}
                    <button onClick={previousPage} disabled={!canPreviousPage}>{t.prev}</button>{" "}
                    <button onClick={nextPage} disabled={!canNextPage}>{t.next}</button>{" "}
                    <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>{t.last}</button>
                </div>
            </div>
        </div>
    );
};

const Status = (props) => (
    <>
        <StatusTable lang={props.lang} statusPeriods={props.singleArgs?.statusPeriods || []} />
    </>
);

export default Status;
